/**
 * Pregnancy dating, on ISO days (`2026-09-30`) so no time zone ever shifts a date.
 *
 * Every method reduces to one date, the start of pregnancy (fécondation). France counts
 * the term from it: start + 9 calendar months, which lands at 41 weeks of amenorrhoea (SA).
 * Most other countries use 40 SA (the last period + 280 days); both are shown, since
 * French and foreign sites disagree by that week and parents notice.
 */

export type DatingInput =
  | { method: "lmp"; lmp: string; cycleDays: number }
  | { method: "conception"; conception: string }
  | { method: "ultrasound"; scanDate: string; weeks: number; days: number }
  | { method: "ivf"; transferDate: string; embryoDay: 3 | 5 };

const DAY_MS = 86_400_000;

const toUtc = (iso: string) => {
  const [year = 1970, month = 1, day = 1] = iso.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

const fromUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export const addDays = (iso: string, days: number) => fromUtc(toUtc(iso) + days * DAY_MS);

export const daysBetween = (from: string, to: string) =>
  Math.round((toUtc(to) - toUtc(from)) / DAY_MS);

/** Calendar months, the last day of a shorter month standing in for a missing 31st. */
export const addMonths = (iso: string, months: number) => {
  const [year = 1970, month = 1, day = 1] = iso.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return fromUtc(target.getTime());
};

/** The day of fécondation each method implies. */
export const pregnancyStart = (input: DatingInput): string => {
  switch (input.method) {
    // Ovulation comes 14 days before the next period, whatever the cycle's length.
    case "lmp":
      return addDays(input.lmp, input.cycleDays - 14);
    case "conception":
      return input.conception;
    // The scan gives an age in SA, counted from a theoretical period two weeks before start.
    case "ultrasound":
      return addDays(input.scanDate, 14 - (input.weeks * 7 + input.days));
    // A day-3 or day-5 embryo was fertilised that many days before the transfer.
    case "ivf":
      return addDays(input.transferDate, -input.embryoDay);
  }
};

export type Dating = {
  start: string;
  /** The period-equivalent day that SA count from: start - 14 days. */
  amenorrhoeaStart: string;
  /** French term: start + 9 months (41 SA). */
  dueDate: string;
  /** International term: 40 SA. */
  dueDate40: string;
  /** The routine French scans, as windows in SA. */
  scans: { label: string; from: string; to: string }[];
};

const sa = (amenorrhoeaStart: string, weeks: number, days = 0) =>
  addDays(amenorrhoeaStart, weeks * 7 + days);

export const dating = (input: DatingInput): Dating => {
  const start = pregnancyStart(input);
  const amenorrhoeaStart = addDays(start, -14);
  return {
    start,
    amenorrhoeaStart,
    dueDate: addMonths(start, 9),
    dueDate40: sa(amenorrhoeaStart, 40),
    scans: [
      {
        label: "Échographie du 1er trimestre",
        from: sa(amenorrhoeaStart, 11),
        to: sa(amenorrhoeaStart, 13, 6),
      },
      {
        label: "Échographie du 2e trimestre",
        from: sa(amenorrhoeaStart, 20),
        to: sa(amenorrhoeaStart, 25),
      },
      {
        label: "Échographie du 3e trimestre",
        from: sa(amenorrhoeaStart, 30),
        to: sa(amenorrhoeaStart, 35),
      },
    ],
  };
};

/** Age on a given day, in SA and in weeks of pregnancy (SA - 2). Null outside 0-45 SA. */
export const ageOn = (amenorrhoeaStart: string, today: string) => {
  const elapsed = daysBetween(amenorrhoeaStart, today);
  if (elapsed < 0 || elapsed > 45 * 7) return null;
  return { weeks: Math.floor(elapsed / 7), days: elapsed % 7 };
};
